import { Box, Heading } from '@zillow/constellation-10';
import React from 'react';

import { mapPostsToGridFormat } from '../../util/postAdapters';
import PostGrid from '../PostGrid/PostGrid';

import type { AuthorMainContentProps } from './AuthorPage.types';

/**
 * Main content area for the author page, displaying the author's latest articles.
 */
const AuthorMainContent: React.FC<Omit<AuthorMainContentProps, 'total'>> = ({
    author,
    posts,
    pages,
    initialPage,
}) => {
    // Construct baseLink for author pagination
    // Prefer author.link or author.slug if available, fallback to /learn/author/[id]
    const baseLink = author.link || `/learn/author/${author.id}`;

    return (
        <Box
            data-epi-type="content"
            css={{
                flex: 1,
                backgroundColor: 'white',
                // v8 $radii.xl = 16px (CMS theme). v10 has no semantic obj.* token
                // for 16px (obj.default is 12px), so use the primitive radii.400
                // = 1rem = 16px. Matches the AgentLoginModal precedent.
                borderRadius: '400',
                paddingBlock: 'loose',
                // NOTE: v8 had `boxShadow: '$shadows.lg'` here, but the v8 shadow
                // scale is none/shadow100-400/focus — there is no `lg`, so this
                // resolved to nothing and no shadow has ever rendered. v10 does
                // have a real shadows.lg; deliberately not adding it here so the
                // migration is visually faithful. Raise with design if the card
                // was always meant to be elevated.
                maxWidth: 'breakpoint-lg',
                minHeight: '400px',
                lg: {
                    marginTop: 0, // Sits below header, not overlapping
                },
            }}
        >
            {/* v8 <Text as="h2" fontType={{ default: 'h4', lg: 'h3' }}> → Heading
                level={2} with a responsive textStyle. v8 h4 = 24px = heading-sm,
                h3 = 36px = heading-md. v8 marginBottom md (24px) → loose.

                No explicit color: v10's headingRecipe base sets color: text.neutral
                rather than inheriting, but that is the same #111116 the v8 Text
                inherited from the body default, so the rendered color is unchanged.
                Deliberately NOT set to 'inherit' — this heading always sits on the
                white Box above (backgroundColor: 'white'), and AuthorPage is a
                fixed-layout page rather than a composable block, so there is no
                container whose color it should pick up. 'inherit' would make it
                depend on whatever wraps the page, which is the riskier option. */}
            <Heading
                level={2}
                css={{
                    textStyle: { base: 'heading-sm', lg: 'heading-md' },
                    marginBottom: 'loose',
                }}
            >
                {author.name}&apos;s Latest Articles
            </Heading>
            {/*
              The posts prop comes from shared Post type, but PostGrid expects posts from types/posts.
              The structures are compatible for display, so we cast here for type compatibility.
            */}
            <PostGrid
                posts={mapPostsToGridFormat(posts)}
                pages={pages}
                page={initialPage}
                baseLink={baseLink}
            />
        </Box>
    );
};

export default AuthorMainContent;
